import { useCallback, useEffect, useRef, useState } from 'react';
import { Linking } from 'react-native';
import { useNotification } from '@oolio-group/hooks';
import { v4 as uuidv4 } from 'uuid';
import {
  App,
  buildCdsAppEventResultSubject,
  CdsAppEvent,
  CdsAppEventAction,
  Order,
  OrderAction,
  OrderPayment,
  OrderPaymentProcessedEvent,
  OrderPaymentResultPayload,
  OrderPaymentStatus,
  TipOption,
} from '@oolio-group/domain';
import { generateOrderEvent, SourceOptions } from '@oolio-group/order-helper';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { logCdsEvent, logCdsEventOnce } from '../../actionLogging/cdsAudit';
import { tap2PayEmbeddedManager } from '../../utils/Tap2PayEmbeddedManager';
import { handledPaymentRequests } from '../../utils/handledPaymentRequests';
import { Session } from '../../state/Session';
import Sentry from '../../utils/Sentry/Sentry';
import { localComms, isLocalCommsEligible } from '../../utils/localComms';
import { useSyncOrderEvents } from '../useSyncOrderEvents/useSyncOrderEvents';
import { networkStatusSubject } from '../connectivity/useNetworkListener';
import { cdsOrderEventsSubject } from './cdsOrderEventsObservable';
import { paymentErrorReasonSubject } from './paymentErrorReasonObservable';
import { isTap2PayOutcomeUnknown } from '@oolio-group/client-utils';

const TAP2PAY_CONFIG = {
  APP_URL_SCHEME: 'ooliotap2pay://ping',
} as const;

const ACTIVE_PAYMENT_STATUSES = new Set([
  OrderPaymentStatus.WAITING,
  OrderPaymentStatus.PENDING,
  OrderPaymentStatus.REQUESTED,
]);

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

// The embedded SDK reports a customer/staff cancellation as status "CANCELLED",
// and an issuer decline or processing failure as "FAILED"/"DECLINED". Preserve
// that distinction on the order so a declined card is recorded as REJECTED
// (declined) rather than being indistinguishable from a user cancellation.
const SDK_CANCELLED_STATUS = 'CANCELLED';
function mapFailureToPaymentStatus(status?: string): OrderPaymentStatus {
  return (status ?? '').toUpperCase() === SDK_CANCELLED_STATUS
    ? OrderPaymentStatus.CANCELLED
    : OrderPaymentStatus.REJECTED;
}

export interface TipModalState {
  visible: boolean;
  amount: number;
  tipOptions?: TipOption[];
  onContinue: (tipAmount: number) => void;
  onSkip: () => void;
  onClose: () => void;
}

// The order object is the authoritative, already-reduced snapshot (via
// order-helper's computeOrderState) regardless of whether it was built from
// local-comms (full resync) or GQL (delta) events — so a payment's status
// here is always current, unlike scanning the raw event stream directly.
function findActivePayment(order?: Order): OrderPayment | undefined {
  return order?.payments?.find(
    p => p.status !== undefined && ACTIVE_PAYMENT_STATUSES.has(p.status),
  );
}

function buildSourceOptions(
  order: Order,
  payment: OrderPayment,
): Partial<SourceOptions> {
  return {
    organizationId: order.organization?.id,
    venueId: order.venue?.id,
    storeId: order.store?.id,
    deviceId: payment.updatedByDeviceId,
    triggeredBy: payment.triggeredBy,
  };
}

export const useTap2PayAutoLaunch = (
  order?: Order,
  session?: Partial<Session>,
) => {
  const activePaymentRequestId = useRef<string | null>(null);
  const pendingPayRef = useRef<{
    amount: number;
    currency: string;
    merchantReference: string;
    orderId: string;
    paymentRequestId: string;
    sourceOptions: Partial<SourceOptions>;
  } | null>(null);

  const { syncOrderEvents } = useSyncOrderEvents();
  const { showNotification } = useNotification();

  const [tipModal, setTipModal] = useState<TipModalState>({
    visible: false,
    amount: 0,
    onContinue: noop,
    onSkip: noop,
    onClose: noop,
  });

  const syncCancellationEvent = useCallback(
    async (
      orderId: string,
      orderNumber: string | undefined,
      paymentRequestId: string,
      sourceOptions: Partial<SourceOptions>,
      reason: string,
      paymentStatus: OrderPaymentStatus = OrderPaymentStatus.CANCELLED,
      // The gateway transaction ref for this attempt (from the SDK result).
      // POS reconciles by merchantReference (shared across retries) and excludes
      // known attempts by this ref, so a declined attempt MUST carry it —
      // otherwise POS re-matches this failure onto a later live attempt and
      // cancels it. See verifyPaymentViaGateway's excludeIds.
      paymentTransactionRef?: string,
    ) => {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: `Syncing ${paymentStatus} + ORDER_SAVE: ${reason}`,
        level: 'warning',
        data: { orderId, paymentRequestId, paymentTransactionRef },
      });

      const eventSource = {
        ...sourceOptions,
        source: App.POS_APP,
      };
      const cancelEvent = generateOrderEvent<OrderPaymentProcessedEvent>(
        OrderAction.ORDER_PAYMENT_PROCESSED,
        eventSource,
        {
          orderId,
          paymentStatus,
          isPrePayment: false,
          paymentRequestId,
          paymentSurcharge: 0,
          ...(paymentTransactionRef ? { paymentTransactionRef } : {}),
        },
      );
      const saveEvent = generateOrderEvent(
        OrderAction.ORDER_SAVE,
        eventSource,
        {
          orderId,
        },
      );

      // Drive the CDS's own screen switcher locally: the CDS is normally a pure
      // consumer of inbound events, so a cancellation it produces itself never
      // comes back to update its UI, leaving it stuck on "Processing Payment".
      // Feed the events into the local stream so it transitions to the error
      // screen immediately (de-duped by event id if they also echo back).
      cdsOrderEventsSubject.next([cancelEvent, saveEvent]);

      try {
        const syncResponse = await syncOrderEvents([cancelEvent, saveEvent]);
        if (syncResponse instanceof Error) throw syncResponse;
        Sentry.addBreadcrumb({
          category: 'tap2pay-embedded',
          message: 'Cancellation event synced',
          level: 'info',
          data: { orderId, paymentRequestId, syncResponse },
        });
        logCdsEvent({
          actionType: 'T2P_CANCEL_SYNCED',
          tag: AuditLogTags.PAYMENT,
          phrase: `${paymentStatus.toLowerCase()} synced to POS: ${reason}`,
          orderId,
          orderNumber,
          requestId: paymentRequestId,
          context: { reason, paymentStatus, paymentTransactionRef },
        });
      } catch (syncError: unknown) {
        Sentry.captureException(syncError, {
          extra: {
            orderId,
            paymentRequestId,
            context: 'tap2pay-embedded-cancel-sync-cds',
          },
        });
        logCdsEvent({
          actionType: 'T2P_CANCEL_SYNC_FAILED',
          tag: AuditLogTags.PAYMENT,
          phrase: `failed to sync ${paymentStatus.toLowerCase()} to POS`,
          orderId,
          orderNumber,
          requestId: paymentRequestId,
          level: 'error',
          context: {
            reason,
            paymentStatus,
            errorMessage:
              syncError instanceof Error
                ? syncError.message
                : String(syncError),
          },
        });
      }
    },
    [syncOrderEvents],
  );

  const pushPaymentResultToPos = useCallback(
    async (
      orderId: string,
      orderNumber: string,
      paymentRequestId: string,
      sourceOptions: Partial<SourceOptions>,
      normalizedData: {
        acquirerReference?: string;
        tipAmount?: number;
        surchargeAmount?: number;
      },
    ) => {
      const storeId = sourceOptions.storeId;
      const deviceId = sourceOptions.deviceId;
      const paymentTransactionRef = normalizedData.acquirerReference;

      if (
        !isLocalCommsEligible() ||
        !storeId ||
        !deviceId ||
        !paymentTransactionRef
      ) {
        return;
      }

      const payload: OrderPaymentResultPayload = {
        paymentRequestId,
        orderId,
        orderNumber,
        paymentTransactionRef,
        tipAmount: normalizedData.tipAmount ?? 0,
        surchargeAmount: normalizedData.surchargeAmount ?? 0,
        completedAt: new Date().toISOString(),
      };

      const event: CdsAppEvent = {
        id: uuidv4(),
        action: CdsAppEventAction.ORDER_PAYMENT_RESULT,
        organizationId: sourceOptions.organizationId ?? '',
        venueId: sourceOptions.venueId,
        storeId,
        deviceId,
        timestamp: Date.now(),
        payload: JSON.stringify(payload),
      };

      const error = await localComms.pushAppEventToStream(
        buildCdsAppEventResultSubject(storeId, deviceId),
        event,
      );

      logCdsEvent({
        actionType: error ? 'T2P_RESULT_PUSH_FAILED' : 'T2P_RESULT_PUSHED',
        tag: AuditLogTags.PAYMENT,
        phrase: error
          ? 'failed to push payment result to POS'
          : 'payment result pushed to POS',
        orderId,
        orderNumber,
        requestId: paymentRequestId,
        level: error ? 'warn' : 'info',
        context: {
          paymentTransactionRef,
          tip: payload.tipAmount,
          surcharge: payload.surchargeAmount,
          ...(error ? { errorMessage: error.message } : {}),
        },
      });
    },
    [],
  );

  const executePayment = useCallback(
    async (
      amount: string,
      currency: string,
      merchantReference: string,
      tipAmount: string | undefined,
      orderId: string,
      paymentRequestId: string,
      sourceOptions: Partial<SourceOptions>,
    ) => {
      // Clear any reason from a previous attempt so the error screen never
      // shows a stale message for this new payment.
      paymentErrorReasonSubject.next(undefined);

      const chargeStartedAt = Date.now();
      logCdsEvent({
        actionType: 'T2P_CHARGE_STARTED',
        tag: AuditLogTags.PAYMENT,
        phrase: 'card tap started on CDS',
        orderId,
        orderNumber: merchantReference,
        requestId: paymentRequestId,
        context: {
          amount: Number(amount),
          tipAmount: tipAmount ? Number(tipAmount) : 0,
          currency,
        },
      });

      try {
        const result = await tap2PayEmbeddedManager.pay({
          amount,
          currency,
          merchantReference,
          tipAmount,
        });

        logCdsEvent({
          actionType: 'T2P_ADYEN_PAY_COMPLETED',
          tag: AuditLogTags.PAYMENT,
          phrase: 'Adyen Pay returned',
          orderId,
          orderNumber: merchantReference,
          requestId: paymentRequestId,
          durationMs: Date.now() - chargeStartedAt,
          context: {
            success: result.success,
            status: result.status,
            acquirerReference: result.normalizedData?.acquirerReference,
          },
        });

        if (result.success) {
          logCdsEvent({
            actionType: 'T2P_COMPLETED',
            tag: AuditLogTags.PAYMENT,
            phrase: 'payment completed',
            orderId,
            orderNumber: merchantReference,
            requestId: paymentRequestId,
            durationMs: Date.now() - chargeStartedAt,
            context: {
              amount: Number(amount),
              tip: result.normalizedData?.tipAmount ?? 0,
              surcharge: result.normalizedData?.surchargeAmount ?? 0,
              cardType: result.normalizedData?.cardType,
              acquirerReference: result.normalizedData?.acquirerReference,
            },
          });

          await pushPaymentResultToPos(
            orderId,
            merchantReference,
            paymentRequestId,
            sourceOptions,
            result.normalizedData ?? {},
          );
        } else if (isTap2PayOutcomeUnknown(result)) {
          logCdsEvent({
            actionType: 'T2P_OUTCOME_UNKNOWN',
            tag: AuditLogTags.PAYMENT,
            phrase:
              'payment outcome unknown, leaving it for the POS gateway check',
            orderId,
            orderNumber: merchantReference,
            requestId: paymentRequestId,
            durationMs: Date.now() - chargeStartedAt,
            level: 'warn',
            context: {
              amount: Number(amount),
              status: result.status,
              errorMessage: result.errorMessage,
            },
          });
        } else {
          const paymentStatus = mapFailureToPaymentStatus(result.status);
          paymentErrorReasonSubject.next(result.errorMessage);
          const isCancelled = paymentStatus === OrderPaymentStatus.CANCELLED;
          logCdsEvent({
            actionType: isCancelled ? 'T2P_CANCELLED' : 'T2P_DECLINED',
            tag: AuditLogTags.PAYMENT,
            phrase: isCancelled
              ? 'payment cancelled on the card reader'
              : 'payment declined',
            orderId,
            orderNumber: merchantReference,
            requestId: paymentRequestId,
            durationMs: Date.now() - chargeStartedAt,
            level: 'warn',
            context: {
              amount: Number(amount),
              status: result.status,
              errorMessage: result.errorMessage,
              acquirerReference: result.normalizedData?.acquirerReference,
            },
          });
          if (!isCancelled) {
            Sentry.captureException(
              new Error(
                result.errorMessage ||
                  `Tap2Pay Embedded payment failed (${result.status})`,
              ),
              {
                extra: {
                  orderId,
                  paymentRequestId,
                  merchantReference,
                  sdkStatus: result.status,
                  errorMessage: result.errorMessage,
                  acquirerReference: result.normalizedData?.acquirerReference,
                  context: 'tap2pay-embedded-payment-failed-cds',
                },
              },
            );
          }
          await syncCancellationEvent(
            orderId,
            merchantReference,
            paymentRequestId,
            sourceOptions,
            `payment unsuccessful (sdk status: ${result.status})`,
            paymentStatus,
            // POS's gateway reconcile keys transactions by the acquirer/PSP
            // ref, NOT the SaleTransactionID (result.transactionId, which is
            // the order number). Record the acquirer ref so excludeIds matches.
            result.normalizedData?.acquirerReference,
          );
        }
      } catch (error: unknown) {
        Sentry.captureException(error, {
          extra: {
            merchantReference,
            orderId,
            paymentRequestId,
            context: 'tap2pay-embedded-payment-cds',
          },
        });
        logCdsEvent({
          actionType: 'T2P_ERROR',
          tag: AuditLogTags.PAYMENT,
          phrase: 'card tap threw',
          orderId,
          orderNumber: merchantReference,
          requestId: paymentRequestId,
          durationMs: Date.now() - chargeStartedAt,
          level: 'error',
          context: {
            amount: Number(amount),
            errorMessage:
              error instanceof Error ? error.message : String(error),
          },
        });
      } finally {
        handledPaymentRequests.add(paymentRequestId);
        activePaymentRequestId.current = null;
        pendingPayRef.current = null;
      }
    },
    [syncCancellationEvent, pushPaymentResultToPos],
  );

  const handleDeeplinkLaunch = useCallback(
    (currentOrder: Order, requestId: string) => {
      const auditContext = {
        orderId: currentOrder.id,
        orderNumber: currentOrder.orderNumber,
        requestId,
      };
      Linking.canOpenURL(TAP2PAY_CONFIG.APP_URL_SCHEME)
        .then(async canOpen => {
          if (!canOpen) {
            logCdsEvent({
              ...auditContext,
              actionType: 'T2P_DEEPLINK_FAILED',
              tag: AuditLogTags.PAYMENT,
              phrase: 'tap to pay app not installed',
              level: 'warn',
            });
            return;
          }
          await Linking.openURL(TAP2PAY_CONFIG.APP_URL_SCHEME);
          logCdsEvent({
            ...auditContext,
            actionType: 'T2P_DEEPLINK_LAUNCHED',
            tag: AuditLogTags.PAYMENT,
            phrase: 'tap to pay app opened',
          });
        })
        .catch((error: unknown) => {
          Sentry.captureException(error, {
            extra: { context: 'tap2pay-deeplink-launch-cds' },
          });
          logCdsEvent({
            ...auditContext,
            actionType: 'T2P_DEEPLINK_FAILED',
            tag: AuditLogTags.PAYMENT,
            phrase: 'failed to open tap to pay app',
            level: 'error',
            context: {
              errorMessage:
                error instanceof Error ? error.message : String(error),
            },
          });
        });
    },
    [],
  );

  const launchEmbeddedPayment = useCallback(
    (
      currentOrder: Order,
      payment: OrderPayment,
      currentSession: Partial<Session> | undefined,
    ) => {
      const requestId = payment.paymentRequestId as string;
      const sourceOptions = buildSourceOptions(currentOrder, payment);
      const auditOrder = {
        orderId: currentOrder.id,
        orderNumber: currentOrder.orderNumber,
        requestId,
      };

      logCdsEventOnce(`T2P_LAUNCH_RECEIVED:${requestId}`, {
        ...auditOrder,
        actionType: 'T2P_LAUNCH_RECEIVED',
        tag: AuditLogTags.PAYMENT,
        phrase: 'payment request received from POS',
        context: { amount: payment.tendered },
      });

      // Don't let the customer get as far as the tip screen or the Adyen
      // card-tap screen if we have no connectivity — neither can complete
      // without it, and the attempt would just hang or fail confusingly.
      // The CDS has no on-screen notification surface, so the customer sees
      // nothing here — cancelling the request is what surfaces the failure,
      // via the POS terminal the cashier is already looking at.
      if (networkStatusSubject.getValue() === false) {
        showNotification({
          message: 'No internet connection',
          error: true,
        });
        Sentry.addBreadcrumb({
          category: 'tap2pay-embedded',
          message: 'Payment blocked: no internet connection at payment start',
          level: 'warning',
          data: {
            orderId: currentOrder.id,
            paymentRequestId: requestId,
          },
        });
        logCdsEvent({
          ...auditOrder,
          actionType: 'T2P_LAUNCH_SKIPPED',
          tag: AuditLogTags.PAYMENT,
          phrase: 'payment blocked: no internet connection',
          level: 'warn',
          context: { reason: 'OFFLINE' },
        });
        syncCancellationEvent(
          currentOrder.id,
          currentOrder.orderNumber,
          requestId,
          sourceOptions,
          'no internet connection at payment start',
        );
        handledPaymentRequests.add(requestId);
        return;
      }

      activePaymentRequestId.current = requestId;

      const checkoutOptions = currentSession?.currentStore?.checkoutOptions;
      const enableTips = checkoutOptions?.enableTips ?? false;
      const currency =
        currentSession?.currentOrganization?.currencyCode
          ?.trim()
          .toUpperCase() || 'AUD';
      // Already validated > 0 by the launch effect before calling this.
      const tendered = payment.tendered;

      if (enableTips) {
        pendingPayRef.current = {
          amount: tendered,
          currency,
          merchantReference: currentOrder.orderNumber,
          orderId: currentOrder.id,
          paymentRequestId: requestId,
          sourceOptions,
        };
        logCdsEvent({
          ...auditOrder,
          actionType: 'T2P_TIP_SHOWN',
          tag: AuditLogTags.PAYMENT,
          phrase: 'tip options shown to customer',
          context: { amount: tendered },
        });
        setTipModal({
          visible: true,
          amount: tendered,
          tipOptions: checkoutOptions?.tipOptions,
          onContinue: (tipAmount: number) => {
            setTipModal(prev => ({ ...prev, visible: false }));
            const pending = pendingPayRef.current;
            if (!pending) return;
            logCdsEvent({
              ...auditOrder,
              actionType:
                tipAmount > 0 ? 'T2P_TIP_SELECTED' : 'T2P_TIP_SKIPPED',
              tag: AuditLogTags.PAYMENT,
              phrase: tipAmount > 0 ? 'tip selected' : 'tip skipped',
              context: { tipAmount, amount: pending.amount },
            });
            executePayment(
              pending.amount.toFixed(2),
              pending.currency,
              pending.merchantReference,
              tipAmount > 0 ? tipAmount.toFixed(2) : undefined,
              pending.orderId,
              pending.paymentRequestId,
              pending.sourceOptions,
            );
          },
          onSkip: () => {
            setTipModal(prev => ({ ...prev, visible: false }));
            const pending = pendingPayRef.current;
            if (!pending) return;
            logCdsEvent({
              ...auditOrder,
              actionType: 'T2P_TIP_SKIPPED',
              tag: AuditLogTags.PAYMENT,
              phrase: 'tip skipped',
              context: { amount: pending.amount },
            });
            executePayment(
              pending.amount.toFixed(2),
              pending.currency,
              pending.merchantReference,
              undefined,
              pending.orderId,
              pending.paymentRequestId,
              pending.sourceOptions,
            );
          },
          onClose: () => {
            setTipModal(prev => ({ ...prev, visible: false }));
            const pending = pendingPayRef.current;
            pendingPayRef.current = null;
            activePaymentRequestId.current = null;
            if (pending) {
              logCdsEvent({
                ...auditOrder,
                actionType: 'T2P_TIP_DISMISSED',
                tag: AuditLogTags.PAYMENT,
                phrase: 'tip screen dismissed by customer',
                level: 'warn',
                context: { amount: pending.amount },
              });
              handledPaymentRequests.add(pending.paymentRequestId);
              syncCancellationEvent(
                pending.orderId,
                pending.merchantReference,
                pending.paymentRequestId,
                pending.sourceOptions,
                'tip modal dismissed by customer',
              );
            }
          },
        });
      } else {
        executePayment(
          tendered.toFixed(2),
          currency,
          currentOrder.orderNumber,
          undefined,
          currentOrder.id,
          requestId,
          sourceOptions,
        );
      }
    },
    [executePayment, syncCancellationEvent, showNotification],
  );

  // Launch: watch the authoritative order snapshot for a new active payment
  // request that isn't already being tracked, instead of the raw
  // cdsOrderEventsSubject event stream. This makes launch/no-launch decisions
  // uniform across local-comms (full resync payloads) and GQL (delta
  // payloads) transports, since both funnel through the same order-helper
  // reducer before `order` reaches this hook.
  useEffect(() => {
    const device = session?.device as
      | { linkCdsToPayApp?: boolean; enableTap2PayEmbedded?: boolean }
      | undefined;
    const isAutoLaunchEnabled = device?.linkCdsToPayApp ?? false;
    const useTap2PayEmbedded = device?.enableTap2PayEmbedded ?? false;

    if (!isAutoLaunchEnabled || !order) return;
    if (activePaymentRequestId.current) return;

    const activePayment = findActivePayment(order);
    if (!activePayment?.paymentRequestId) return;

    // Already dealt with this request here (completed or cancelled). A
    // killed-and-reopened POS can re-send the order with the payment still
    // showing active — don't re-launch the tip modal / payment for it.
    if (handledPaymentRequests.has(activePayment.paymentRequestId)) return;

    if (!useTap2PayEmbedded) {
      activePaymentRequestId.current = activePayment.paymentRequestId;
      handleDeeplinkLaunch(order, activePayment.paymentRequestId);
      return;
    }

    if (tap2PayEmbeddedManager.isPaymentInProgress) return;
    if (!tap2PayEmbeddedManager.isInitialized) {
      activePaymentRequestId.current = activePayment.paymentRequestId;
      Sentry.captureException(
        new Error(
          'Tap2Pay Embedded SDK not initialized when payment arrived, payment cancelled',
        ),
        {
          extra: {
            orderId: order.id,
            orderNumber: order.orderNumber,
            paymentRequestId: activePayment.paymentRequestId,
            context: 'tap2pay-embedded-not-initialized-cds',
          },
        },
      );
      logCdsEvent({
        actionType: 'T2P_LAUNCH_SKIPPED',
        tag: AuditLogTags.PAYMENT,
        phrase: 'payment blocked: card reader not ready',
        orderId: order.id,
        orderNumber: order.orderNumber,
        requestId: activePayment.paymentRequestId,
        level: 'error',
        context: { reason: 'SDK_NOT_INITIALIZED' },
      });
      syncCancellationEvent(
        order.id,
        order.orderNumber,
        activePayment.paymentRequestId,
        buildSourceOptions(order, activePayment),
        'embedded SDK not initialized when payment arrived',
      );
      handledPaymentRequests.add(activePayment.paymentRequestId);
      return;
    }
    if (!activePayment.tendered || activePayment.tendered <= 0) return;

    launchEmbeddedPayment(order, activePayment, session);
  }, [
    order,
    session,
    launchEmbeddedPayment,
    handleDeeplinkLaunch,
    syncCancellationEvent,
  ]);

  // Dismiss: if the currently tracked request resolves through any path
  // (POS cancelling it, or it completing before executePayment's own
  // response comes back) while the tip modal is showing or the SDK call is
  // in flight, get out of the way instead of leaving the modal stuck open.
  useEffect(() => {
    if (!activePaymentRequestId.current || !order) return;

    const tracked = order.payments?.find(
      p => p.paymentRequestId === activePaymentRequestId.current,
    );
    if (!tracked) return;

    const stillActive =
      tracked.status !== undefined &&
      ACTIVE_PAYMENT_STATUSES.has(tracked.status);
    if (stillActive) return;

    Sentry.addBreadcrumb({
      category: 'tap2pay-embedded',
      message:
        'Active/pending payment resolved externally, dismissing tip modal',
      level: 'info',
      data: {
        paymentRequestId: activePaymentRequestId.current,
        resolvedStatus: tracked.status,
      },
    });
    logCdsEvent({
      actionType: 'T2P_ABORT_REQUESTED',
      tag: AuditLogTags.PAYMENT,
      phrase: 'payment resolved elsewhere, CDS standing down',
      orderId: order.id,
      orderNumber: order.orderNumber,
      requestId: tracked.paymentRequestId ?? undefined,
      context: {
        resolvedStatus: tracked.status,
        abortingCardReader: tracked.status !== OrderPaymentStatus.COMPLETE,
      },
    });
    if (tracked.paymentRequestId) {
      handledPaymentRequests.add(tracked.paymentRequestId);
    }
    activePaymentRequestId.current = null;
    pendingPayRef.current = null;
    setTipModal(prev => ({ ...prev, visible: false }));

    // The payment already completed (e.g. POS's own gateway reconcile won
    // a race against a POS-side cancel click) — the local SDK call is the
    // one that produced that success, so let it run to completion
    // (capture, receipt, etc.) instead of aborting a transaction that has
    // already been authorized. Aborting here does nothing for POS (it's
    // already marked the order COMPLETE) and risks disrupting Adyen's own
    // capture of a payment that was, in fact, approved.
    if (tracked.status === OrderPaymentStatus.COMPLETE) {
      return;
    }

    // No-op if the SDK isn't actually mid-transaction (e.g. still on the
    // tip modal, or already resolved successfully).
    tap2PayEmbeddedManager.abortTransaction();
  }, [order]);

  return { tipModal };
};
